<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { VForm } from "vuetify/components";
import type { BookingStepProps } from "../types/index";
import CustomDialog from "../../common/CustomDialog.vue";

const props = defineProps<
  Omit<BookingStepProps, "areaDurationDisable"> & {
    areaDurationDisable?: boolean;
  }
>();

const emit = defineEmits<{
  (e: "update:form", value: any): void;
  (e: "validate"): Promise<boolean>;
  (e: "next-step"): void;
}>();

const activityFormRef = ref<InstanceType<typeof VForm>>();

const showActivityDetails = ref(false);
const selectedActivity = ref<any>(null);

const handleActivitySelect = (activityId: string) => {
  emit("update:form", { ...props.form, activity: activityId });
  emit("next-step");
};

const handleInfoClick = (event: Event, activity: any) => {
  event.stopPropagation(); // Prevent button click event
  selectedActivity.value = activity;
  showActivityDetails.value = true;
};

defineExpose({
  validate: async () => {
    return !!props.form.activity;
  },
});

const truncateDescription = (description: string) => {
  const maxLen = 50;

  return description.length > maxLen
    ? `${description.slice(0, maxLen)}...`
    : description;
};

const formatSessionPrice = (
  price: number,
  bookingSpacing: number,
  bookingGap: number,
) => {
  return `$${price} Per ${bookingSpacing - bookingGap} Min Session`;
};
</script>

<template>
  <VForm ref="activityFormRef">
    <VRow>
      <VCol
        v-for="activity in activityList"
        :key="activity.id"
        cols="12"
        sm="6"
        md="4"
      >
        <VBtn
          block
          size="large"
          height="120"
          :color="activity.color"
          :class="{ selected: form.activity === activity.id }"
          class="d-flex activity-btn align-start position-relative"
          @click="handleActivitySelect(activity.id)"
        >
          <VIcon
            icon="mdi-information-outline"
            class="position-absolute"
            style="top: 8px; right: 8px"
            @click="handleInfoClick($event, activity)"
          />
          <div class="d-flex flex-column align-start text-left w-100">
            <div class="text-h6 mb-2">
              {{ activity.name }}
            </div>
            <div class="text-body-2 text-wrap">
              {{ truncateDescription(activity.description) }}
            </div>
          </div>
        </VBtn>
      </VCol>
    </VRow>
  </VForm>

  <!-- Activity Details Dialog -->
  <CustomDialog v-model="showActivityDetails" :max-width="650">
    <VCard class="mx-auto overflow-visible" max-width="650">
      <VCardText>
        <VRow>
          <VCol cols="12">
            <!-- Activity Image -->
            <div class="image-container">
              <img
                v-if="selectedActivity?.image"
                :src="`${pb.baseUrl}/api/files/${selectedActivity.collectionId}/${selectedActivity.id}/${selectedActivity.image}?token=`"
                alt="Activity Image"
                class="activity-image"
              />
              <div v-else class="no-image-placeholder">No Image Available</div>
            </div>
          </VCol>
        </VRow>
        <VRow>
          <VCol lg="4" md="4" sm="12" xs="12" cols="12">
            <VCol>
              <strong>Name:</strong>
              <div>{{ selectedActivity?.name }}</div>
            </VCol>
            <VCol>
              <strong>Normal Price:</strong>
              <div v-if="selectedActivity?.sessionFullPrice">
                ${{ selectedActivity.sessionFullPrice }} Per
                {{
                  selectedActivity.bookingSpacing - selectedActivity.bookingGap
                }}
                Min Session
              </div>
            </VCol>
          </VCol>
          <VCol lg="8" md="8" sm="12" xs="12" cols="12">
            <VCol
              v-if="
                selectedActivity?.longDescription ||
                selectedActivity?.description
              "
            >
              <strong>Description:</strong>
              <div
                v-html="
                  selectedActivity?.longDescription ||
                  selectedActivity?.description
                "
              ></div>
            </VCol>
          </VCol>
        </VRow>
      </VCardText>

      <VCardActions class="d-flex justify-end">
        <VBtn color="error" variant="flat" @click="showActivityDetails = false">
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </CustomDialog>
</template>

<style lang="scss" scoped>
.activity-btn {
  min-height: 120px;
  white-space: normal;
  height: auto !important;
  padding: 16px;

  &.selected {
    border: 2px solid currentColor;
  }

  &:not(.selected) {
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }

  :deep(.v-btn__content) {
    width: 100%;
    height: 100%;
    white-space: normal;
  }
}

.image-container {
  width: 100%;
  min-height: 200px; // Minimum height for small content
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 16px;
}

.activity-image {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.no-image-placeholder {
  color: rgba(0, 0, 0, 0.6);
  font-size: 1.25rem;
}
</style>
